import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowUpRight,
  FileSignature,
  Video,
  ShieldCheck,
  BadgeCheck,
  Award,
  Lock,
  Stamp,
  Globe,
  HeartHandshake,
  Stethoscope,
  Briefcase,
  Mail,
  Phone,
  Ship,
} from "lucide-react";
import {
  PillarCard,
  TrustChip,
  GoldButton,
  GhostGoldButton,
  SectionEyebrow,
} from "@/components/ui-bits";
import { BuyMeACoffeeButton } from "@/components/BuyMeACoffeeButton";
import { MembershipCallout } from "@/components/MembershipCallout";
import { HOME } from "@/constants/testIds";
import { IMG } from "@/constants/images";

export default function HomePage() {
  return (
    <>
      <Helmet>
        <title>
          OGO Notary & Signing Services | Mobile Notary, RON & Apostille — Brevard County, FL
        </title>
        <meta
          name="description"
          content="OGO Notary & Signing Services: Elite Mobile Notary, RON, and Ordained Minister Wedding Officiant services in Brevard County, FL. Managed by Dr. Gregory A. Byers. Built on Experience + Grounded in Faith. Specializing in loan signings, commercial document support, elder care notary, and wedding ceremonies."
        />
        <meta
          name="keywords"
          content="mobile notary Brevard County, Remote Online Notary Florida, RON Florida, wedding officiant Brevard County, ordained minister Cocoa Beach, wedding officiant Melbourne FL, beach wedding officiant Florida, notary Cocoa Beach, notary Melbourne FL, notary Palm Bay, notary Titusville, Apostille Florida, loan signing agent Brevard, NNA certified signing agent, elder care notary, mobile notary Space Coast, Dr. Gregory Byers"
        />
        <link rel="canonical" href="https://ogonotary.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="OGO Notary & Signing Services" />
        <meta
          property="og:title"
          content="OGO Notary & Signing Services | Mobile, RON & Apostille in Brevard County, FL"
        />
        <meta
          property="og:description"
          content="Certified mobile notary, Remote Online Notarization (RON), loan signing, and Apostille agent on Florida's Space Coast. Serving Cocoa Beach, Melbourne, Palm Bay, Titusville, Merritt Island, Viera — plus RON worldwide."
        />
        <meta property="og:url" content="https://ogonotary.com/" />
        <meta
          property="og:image"
          content="https://ogonotary.com/img/ogo-logo.png"
        />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="OGO Notary & Signing Services | Mobile, RON & Apostille in Brevard County, FL"
        />
        <meta
          name="twitter:description"
          content="Certified mobile notary, RON, loan signing, and Apostille agent serving Brevard County, Florida — and RON worldwide."
        />
        <meta
          name="twitter:image"
          content="https://ogonotary.com/img/ogo-logo.png"
        />
      </Helmet>
      {/* HERO */}
      <section
        data-testid={HOME.hero}
        className="relative overflow-hidden border-b border-white/10"
      >
        {/* Space Coast beach + cruise-port vibe — Cape Canaveral / Merritt Island */}
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-cover pointer-events-none"
          style={{
            backgroundImage: `url(${IMG.spaceCoastHero})`,
            backgroundPosition: "center center",
            opacity: 0.68,
          }}
        />
        {/* Overall navy blend — subdued, not dark */}
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-b from-[#142339]/35 via-[#142339]/25 to-[#142339]/70 pointer-events-none"
        />
        {/* Left vignette — softened so the cruise ship stays visible behind the headline */}
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-r from-[#142339]/80 via-[#142339]/40 to-transparent pointer-events-none"
        />

        <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-24 pb-28 sm:pt-32 sm:pb-36">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7 fade-up">
              <SectionEyebrow className="mb-2">
                Space Coast · Brevard County · Worldwide RON
              </SectionEyebrow>
              <div className="mb-6 text-[11px] sm:text-xs tracking-[0.22em] uppercase text-[#D4AF37]/70 font-medium">
                Cape Canaveral · Kennedy Space Center · Merritt Island
              </div>
              <h1
                data-testid={HOME.heroHeadline}
                className="font-serif-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.02] tracking-tighter"
              >
                The Space Coast&apos;s Premier{" "}
                <span className="text-[#D4AF37]">High-Security Signing Agent</span>{" "}
                Mobile & Remote Online Notary Resource.
              </h1>
              <p
                data-testid={HOME.heroSubheadline}
                className="mt-8 text-lg sm:text-xl text-slate-300 max-w-2xl leading-relaxed"
              >
                Trusted for 45 years with classified, sensitive, and
                multi-national legal document packages. Delivering flawless,
                executive-grade mobile notary, loan signing, and Remote Online
                Notarization (RON) services across Brevard County.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <GoldButton
                  as={Link}
                  to="/contact"
                  data-testid={HOME.bookBtn}
                >
                  Book an Appointment <ArrowUpRight className="w-4 h-4" />
                </GoldButton>
                <GhostGoldButton
                  as={Link}
                  to="/pricing"
                  data-testid={HOME.learnMoreBtn}
                >
                  View Pricing
                </GhostGoldButton>
              </div>

              {/* Cruise-Port Signings niche trust chip — Port Canaveral lead hook */}
              <Link
                to="/contact?service=Cruise-Port+Signings"
                data-testid="home-cruise-port-chip"
                className="mt-6 inline-flex items-center gap-2.5 pl-3 pr-4 py-2 border border-[#D4AF37]/40 bg-[#D4AF37]/5 hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/70 transition-colors group"
              >
                <Ship className="w-4 h-4 text-[#D4AF37]" />
                <span className="text-[11px] sm:text-xs uppercase tracking-[0.18em] text-slate-100">
                  Cruise-Port Signings
                  <span className="text-[#D4AF37]/80"> · Port Canaveral</span>
                </span>
                <ArrowUpRight className="w-3.5 h-3.5 text-[#D4AF37] opacity-70 group-hover:opacity-100 transition-opacity" />
              </Link>
            </div>

            <div className="lg:col-span-5">
              <div className="space-y-4">
                <CompetencyCard
                  icon={ShieldCheck}
                  title="Vetted Integrity"
                  body="Backed by 45 years of service supporting the Department of Defense, DLA, and State Department."
                />
                <CompetencyCard
                  icon={Globe}
                  title="Global Execution"
                  body="Veteran operator of thousands of high-stakes, complex, international legal packages, corporate entity formations, and official Secretary of State Apostilles."
                />
                <CompetencyCard
                  icon={HeartHandshake}
                  title="Simple & Safe"
                  body="Seamlessly blending rigid federal-level compliance with empathetic, local on-call service."
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MEMBERSHIP CALL-OUT — placed right after the hero so it's the first CTA a visitor sees */}
      <MembershipCallout variant="hero" testid="home-membership-callout" />

      {/* 5 PILLARS OF OGO EXCELLENCE — unified service architecture */}
      <section
        data-testid="home-five-pillars"
        className="py-24 sm:py-32 border-b border-white/10"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
            <div className="max-w-2xl">
              <SectionEyebrow className="mb-4">The 5 Pillars of OGO Excellence</SectionEyebrow>
              <h2 className="font-serif-display text-4xl sm:text-5xl text-white leading-tight tracking-tight">
                Every stage of life, every complexity of business — one
                disciplined ecosystem.
              </h2>
            </div>
            <p className="text-slate-400 max-w-md text-sm leading-relaxed">
              Five deliberately-engineered service pillars covering the full
              arc of what individuals, families, and businesses need — from a
              same-day mobile signature in Brevard County to Secretary of State
              apostilles, ministerial ceremonies, and dignified elder-care
              notarization.
            </p>
          </div>

          {/* Pillars 1–4 : 2×2 card grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            <PillarCard
              testid="pillar-1-mobile-ron"
              icon={Video}
              title="Pillar 1 · Mobile, RON & On-Demand Notary"
              body="General notarizations, mobile travel across Brevard County & Florida statewide, and Remote Online Notarization (RON) for signers anywhere in the U.S."
              footer={
                <Link
                  to="/mobile-notary"
                  data-testid="pillar-1-cta"
                  className="inline-flex items-center gap-2 hover:text-[#FDE047]"
                >
                  Explore Now <ArrowUpRight className="w-4 h-4" />
                </Link>
              }
            />
            <PillarCard
              testid="pillar-2-real-estate"
              icon={FileSignature}
              title="Pillar 2 · Real Estate, Loan Signing & Estate Planning"
              body="Mortgage refis, purchase / sale packages, commercial loans, Wills, Living Trusts, and Powers of Attorney (POAs) — executed with NNA-certified precision."
              footer={
                <Link
                  to="/signing-agent"
                  data-testid="pillar-2-cta"
                  className="inline-flex items-center gap-2 hover:text-[#FDE047]"
                >
                  Explore Now <ArrowUpRight className="w-4 h-4" />
                </Link>
              }
            />
            <PillarCard
              testid="pillar-3-gov-apostille"
              icon={Briefcase}
              title="Pillar 3 · Government & Business Document Support"
              body="Modern B2B services, corporate entity support, commercial proposals, joint venture documents, and official Secretary of State Apostilles — trusted by law firms, corporates, and foreign governments."
              footer={
                <Link
                  to="/apostille"
                  data-testid="pillar-3-cta"
                  className="inline-flex items-center gap-2 hover:text-[#FDE047]"
                >
                  Explore Now <ArrowUpRight className="w-4 h-4" />
                </Link>
              }
            />
            <PillarCard
              testid="pillar-4-new-beginnings"
              icon={HeartHandshake}
              title="Pillar 4 · New Beginnings & Life Milestones"
              body="Wedding officiant services, marriage certificates, adoption paperwork, and early / mid-life family milestone documentation leading up to senior care planning."
              footer={
                <Link
                  to="/wedding-services"
                  data-testid="pillar-4-cta"
                  className="inline-flex items-center gap-2 hover:text-[#FDE047]"
                >
                  Explore Now <ArrowUpRight className="w-4 h-4" />
                </Link>
              }
            />
          </div>

          {/* Pillar 5 — full-width emphasis banner */}
          <div
            data-testid="pillar-5-elder-care-banner"
            className="mt-8 lg:mt-10 border border-[#D4AF37]/50 bg-gradient-to-br from-[#1A2B45] via-[#1A2B45] to-[#0F1B2E] p-8 sm:p-10 lg:p-14 relative overflow-hidden"
          >
            <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full bg-[#D4AF37]/[0.05] blur-3xl pointer-events-none" />
            <div className="absolute -bottom-8 -left-8 w-40 h-40 rounded-full bg-[#D4AF37]/[0.04] blur-3xl pointer-events-none" />
            <div className="relative grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              <div className="lg:col-span-2 flex lg:justify-center">
                <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-full border-2 border-[#D4AF37] flex items-center justify-center text-[#D4AF37]">
                  <Stethoscope className="w-8 h-8 lg:w-9 lg:h-9" />
                </div>
              </div>
              <div className="lg:col-span-7">
                <div className="eyebrow mb-3 text-[#D4AF37]">
                  Pillar 5 · Our Signature Focus
                </div>
                <h3 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight tracking-tight">
                  Elder Care &amp; Senior Specialist Services
                </h3>
                <p className="mt-5 text-slate-300 leading-relaxed max-w-3xl">
                  Combining Senior Living Transition &amp; Patient Advocacy —
                  a compassionate, disciplined, and dignified service pillar
                  built for our senior community and their families. Bedside
                  notarization, hospital visits, hospice presence, guardianship
                  documentation, elder-care POAs, healthcare surrogacies, and
                  end-of-life planning — all executed with the same operational
                  rigor as any DoD packet, and the empathy of a family friend.
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-semibold">
                    Certified Senior Advisor (CSA) Candidate
                  </span>
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-semibold">
                    End-of-Life (EoL) Doula Candidate
                  </span>
                </div>
              </div>
              <div className="lg:col-span-3 lg:justify-self-end w-full">
                <Link
                  to="/elder-care"
                  data-testid="pillar-5-cta"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#142339] px-6 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FDE047] transition-colors"
                >
                  Explore Now <ArrowUpRight className="w-4 h-4" />
                </Link>
                <div className="mt-3 text-center text-[10px] uppercase tracking-[0.18em] text-slate-400">
                  Bedside · Hospice · Home · ALF
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* G.I.V.E. INITIATIVE — Charitable commitment banner */}
      <section
        data-testid="home-give-initiative"
        className="py-10 sm:py-12 border-b border-white/10 bg-[#0A1020]"
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          {/* Headline + tagline */}
          <div className="text-center">
            <h2
              data-testid="give-headline"
              className="font-serif-display text-xl sm:text-2xl lg:text-[26px] leading-snug tracking-tight"
            >
              <span className="text-[#D4AF37]">The G.I.V.E. Initiative:</span>{" "}
              <span className="text-white/90">
                Give <span className="text-[#D4AF37]">•</span> Impact{" "}
                <span className="text-[#D4AF37]">•</span> Value{" "}
                <span className="text-[#D4AF37]">•</span> Educate
              </span>
            </h2>
            <p className="mt-3 max-w-3xl mx-auto text-slate-400 text-xs sm:text-sm leading-relaxed">
              A minimum of{" "}
              <span className="text-white font-semibold">20% of all OGO</span>{" "}
              digital product profits are pledged to support our veteran and
              global partners.
            </p>
          </div>

          {/* Partner logos strip — displayed at native resolution to keep text sharp */}
          <div className="mt-6 sm:mt-7 flex items-center justify-center">
            <img
              data-testid="give-partners-strip"
              src="/img/give-partners.png"
              alt="G.I.V.E. Initiative partners: Tunnels to Towers, Wounded Warrior Project, International Justice Mission, The Bucket Ministry, Children International"
              width={596}
              height={52}
              className="h-auto max-w-full"
              loading="lazy"
            />
          </div>

          {/* Bethany Home Foundation — Thailand partner, kept in its native pink so it stands apart */}
          <div className="mt-8 flex flex-col items-center">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-3">
              Global Partner · Thailand
            </div>
            <a
              href="https://www.bethanyhomefoundation.org"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="give-bethany-home"
              className="inline-block transition-opacity hover:opacity-90"
              aria-label="Bethany Home Foundation — Thailand"
            >
              <img
                src="/img/bethany-home.jpg"
                alt="Bethany Home Foundation — Thailand"
                width={877}
                height={163}
                className="h-auto max-w-full sm:max-w-md"
                loading="lazy"
              />
            </a>
          </div>
        </div>
      </section>

      {/* SUPPORT THE MISSION — small tip strip below G.I.V.E. */}
      <section
        data-testid="home-support-strip"
        className="py-10 sm:py-12 border-b border-white/10"
      >
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="border border-[#D4AF37]/25 bg-[#0A1020] px-6 sm:px-8 py-6 flex flex-col sm:flex-row items-center gap-5 sm:gap-8 text-center sm:text-left">
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-2">
                Support the Mission
              </div>
              <p className="text-slate-300 text-sm leading-relaxed">
                Enjoying the free guides, checklists, and educational content?
                Fuel the next release with a small tip — every coffee keeps
                Greg building.
              </p>
            </div>
            <BuyMeACoffeeButton
              variant="ghost"
              label="Buy Me a Coffee"
              testid="home-bmc-btn"
              className="shrink-0"
            />
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <section className="py-20 sm:py-24 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-12">
            <SectionEyebrow className="mb-4">Certifications</SectionEyebrow>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-white leading-tight tracking-tight">
              Independently certified across every signing channel we serve.
            </h2>
            <p className="mt-5 text-slate-400 leading-relaxed max-w-2xl text-sm">
              All seals shown are current. Our NNA Member &amp; Certified
              Loan Signing Agent designation, Apostille agent credential,
              RON commission, annual background screening, plus our{" "}
              <span className="text-[#D4AF37]">Certified Senior Advisor (CSA)
              Candidate</span> and{" "}
              <span className="text-[#D4AF37]">End-of-Life (EoL) Doula
              Candidate</span> pathways — all renewed and expanded on
              schedule.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            <CertCard
              testid="cert-card-nna"
              src={IMG.certNNA}
              alt="NNA Certified Notary Signing Agent seal"
              title="Certified Notary Signing Agent"
              sub="National Notary Association"
            />
            <CertCard
              testid="cert-card-background"
              src={IMG.certBackgroundScreened}
              alt="NNA Background Screened seal"
              title="Background Screened"
              sub="Annually Renewed"
            />
            <CertCard
              testid="cert-card-apostille"
              src={IMG.certApostille}
              alt="Premiere Apostille Agent seal — National / International"
              title="Premiere Apostille Agent"
              sub="National · International"
            />
            <CertCard
              testid="cert-card-ron"
              src={IMG.certRON}
              alt="Premiere RON Agent seal — Remote Online Notary"
              title="Premiere RON Agent"
              sub="Remote Online Notary"
            />
          </div>
        </div>
      </section>

      {/* TRUST BANNER */}
      <section
        data-testid={HOME.trustBanner}
        className="py-16 border-b border-white/10 bg-[#1A2B45]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col gap-8">
            <div className="max-w-2xl">
              <SectionEyebrow className="mb-3">Trust & Compliance</SectionEyebrow>
              <p className="font-serif-display text-2xl sm:text-3xl text-white leading-snug">
                Verified credentials. Audited processes. Zero shortcuts.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 w-full">
              <TrustChip icon={Lock} label="DoD Secret Cleared" />
              <TrustChip icon={Stamp} label="Florida Commissioned Notary" />
              <TrustChip icon={ShieldCheck} label="Bonded" />
              <TrustChip icon={BadgeCheck} label="Insured (E&O)" />
              <TrustChip icon={Award} label="NNA Member & LSA" />
              <TrustChip icon={Lock} label="Background Screened" />
            </div>
          </div>
        </div>
      </section>

      {/* B2B Vendor CTA strip */}
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="eyebrow mb-3">For Title Companies, Escrow & Loan Officers</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-white leading-tight tracking-tight">
              Add OGO Notary to your vendor list — and stop chasing missed signatures.
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed max-w-2xl">
              One short form. We send our W-9, commission, bond, E&O, NNA cert, and flat-rate package sheet — usually within one business day.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <GoldButton
              as={Link}
              to="/vendor"
              data-testid="vendor-cta-home"
            >
              Add Us to Your Vendor List <ArrowUpRight className="w-4 h-4" />
            </GoldButton>
          </div>
        </div>
      </section>

      {/* FAQ CTA — full answers live on /faq to concentrate SEO signal */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="border border-[#D4AF37]/40 bg-[#1A2B45]/70 backdrop-blur-md p-10 sm:p-14 grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-8">
              <SectionEyebrow className="mb-4">Answers, before you ask</SectionEyebrow>
              <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight tracking-tight">
                Mobile notary, RON, Apostille &amp; loan-signing questions —
                all answered in one place.
              </h2>
              <p className="mt-6 text-slate-300 max-w-2xl leading-relaxed">
                Plain-English answers to the 29 most common questions about
                mobile notary appointments in Brevard County, Florida Remote
                Online Notarization, Apostille processing, loan-signing
                packages, wills, powers of attorney, hospital and hospice
                visits, pricing, and booking. If you still can&apos;t find your
                answer, email{" "}
                <a
                  href="mailto:Greg@ogonotary.com"
                  className="text-[#D4AF37] hover:underline"
                >
                  Greg@ogonotary.com
                </a>{" "}
                or call{" "}
                <a
                  href="tel:+18555054722"
                  className="text-[#D4AF37] hover:underline"
                >
                  (855) 505-4722
                </a>
                .
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3 lg:items-end">
              <GoldButton
                as={Link}
                to="/faq"
                data-testid="home-faq-cta"
                className="w-full lg:w-auto justify-center"
              >
                Read All FAQs <ArrowUpRight className="w-4 h-4" />
              </GoldButton>
              <Link
                to="/contact"
                data-testid="home-faq-contact-btn"
                className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-300 hover:text-[#D4AF37] transition-colors"
              >
                Ask Your Own Question →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function CompetencyCard({ icon: Icon, title, body }) {
  return (
    <div className="border border-white/10 bg-[#1A2B45]/80 backdrop-blur-md p-6 hover:border-[#D4AF37]/50 transition-colors flex items-start gap-4">
      <div className="w-11 h-11 border border-[#D4AF37]/50 flex items-center justify-center text-[#D4AF37] shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="font-serif-display text-xl text-white mb-1.5 leading-tight">
          {title}
        </div>
        <p className="text-slate-300 text-sm leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

function CertCard({ testid, src, alt, title, sub }) {
  return (
    <div
      data-testid={testid}
      className="border border-white/10 bg-white/[0.03] hover:border-[#D4AF37]/50 transition-colors p-6 sm:p-7 flex flex-col items-center text-center"
    >
      <img
        src={src}
        alt={alt}
        className="h-24 sm:h-28 w-auto mb-5 drop-shadow-[0_0_25px_rgba(212,175,55,0.15)]"
      />
      <div className="font-serif-display text-lg text-white mb-1 leading-tight">
        {title}
      </div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
        {sub}
      </div>
    </div>
  );
}
